import { DataTable, TableHead, TableRow, TableEmptyState } from "../components/DataTable";
import { ArrowRightIcon, ChatsCircleIcon, GavelIcon, ShieldCheckIcon, WarningOctagonIcon } from "@phosphor-icons/react";
import { api } from "../api";
import { EmptyState, ErrorState, PageHeader, SkeletonRows, StatusTag } from "../components/UI";
import { useResource } from "../hooks/useResource";
import type { BanLog, ChatOptions, OverviewStats, Page, User, VoteSession } from "../types";
import { chatName, formatDate, formatNumber } from "../utils";

type DashboardData = {
  overview: OverviewStats | null;
  bans: Page<BanLog>;
  votes: VoteSession[];
};

type Props = {
  user: User;
  chatOptions: ChatOptions;
  onNavigate: (path: string) => void;
};

export function DashboardPage({ user, chatOptions, onNavigate }: Props) {
  const chats = chatOptions.items;
  const resource = useResource<DashboardData>(
    async (signal) => {
      const [overview, bans, votes] = await Promise.all([
        user.is_super_admin ? api.overview(signal) : Promise.resolve(null),
        api.banLogs({ page: 1 }, signal),
        api.activeVotes(signal)
      ]);
      return { overview, bans, votes };
    },
    [user.is_super_admin],
    "Не удалось загрузить обзор"
  );
  const activeChats = chatOptions.active;
  const ingress = chatOptions.ingress;
  const overview = resource.data?.overview;

  return (
    <>
      <PageHeader
        code="СЕЙЧАС"
        title={`Обзор для ${user.first_name}`}
        description="Срабатывания за сутки, последние блокировки и открытые голосования."
        actions={<button className="button primary" onClick={() => onNavigate("/chats")}>Открыть чаты <ArrowRightIcon size={17} /></button>}
      />

      <section className="telemetry-grid" aria-label="Ключевые показатели">
        <article className="telemetry-cell accent-cell"><span>Доступно чатов</span><strong>{formatNumber(overview?.chats_total ?? chatOptions.total)}</strong><small>{user.is_super_admin ? "всего" : "с вашими правами"}</small></article>
        <article className="telemetry-cell"><span>Защита включена</span><strong>{formatNumber(overview?.chats_active ?? activeChats)}</strong><small>из {formatNumber(overview?.chats_total ?? chatOptions.total)}</small></article>
        <article className="telemetry-cell"><span>Срабатываний за сутки</span><strong>{formatNumber(overview?.detections_24h)}</strong><small>{overview ? `${formatNumber(overview.bans_24h)} банов` : `${ingress} включённых проверок`}</small></article>
        <article className="telemetry-cell"><span>Открытые голосования</span><strong>{formatNumber(resource.data?.votes.length)}</strong><small>нужен голос</small></article>
        {user.is_super_admin && <article className="telemetry-cell wide-cell"><span>Глобальная база спамеров</span><strong>{formatNumber(overview?.spamers_total)}</strong><small>{formatNumber(overview?.approved_total)} доверенных пользователей</small></article>}
      </section>

      {resource.error && <ErrorState message={resource.error} onRetry={resource.reload} />}
      {resource.loading && !resource.data ? <SkeletonRows rows={7} columns={4} /> : (
        <div className="dashboard-layout">
          <section className="data-panel activity-panel">
            <header className="panel-header"><div><samp>14 ДНЕЙ</samp><h2>Срабатывания по дням</h2></div><ShieldCheckIcon size={24} weight="thin" /></header>
            {overview?.activity?.length ? (
              <><div className="activity-chart" aria-label="Активность за 14 дней">
                {overview.activity.map((point) => {
                  const peak = Math.max(...overview.activity.map((item) => item.spam), 1);
                  return (
                    <div className="activity-column" key={point.date} title={`${point.date}: ${point.spam} детекций, ${point.bans} банов`}>
                      <span style={{ height: `${Math.max(4, point.spam / peak * 100)}%` }} />
                      <i style={{ height: `${Math.max(2, point.bans / peak * 100)}%` }} />
                      <small>{new Date(point.date).toLocaleDateString("ru", { day: "2-digit", month: "2-digit" })}</small>
                    </div>
                  );
                })}
              </div><details className="chart-data"><summary>Данные графика</summary><table><caption>Активность за 14 дней</caption><thead><tr><th scope="col">Дата</th><th scope="col">Детекции</th><th scope="col">Баны</th></tr></thead><tbody>{overview.activity.map((point) => <tr key={point.date}><th scope="row">{point.date}</th><td>{point.spam}</td><td>{point.bans}</td></tr>)}</tbody></table></details></>
            ) : (
              <div className="chat-quick-list">
                {chats.slice(0, 8).map((chat) => (
                  <button key={chat.id} onClick={() => onNavigate(`/chats/${chat.id}`)}>
                    <span><ChatsCircleIcon size={18} /><strong>{chatName(chat)}</strong></span>
                    <StatusTag tone={chat.is_active ? "ok" : "neutral"}>{chat.is_active ? "Активен" : "Пауза"}</StatusTag>
                  </button>
                ))}
                {!chats.length && <EmptyState title="Нет доступных чатов" text="Проверьте права администратора в Telegram." />}
              </div>
            )}
          </section>

          <section className="data-panel votes-panel">
            <header className="panel-header"><div><samp>НУЖЕН ГОЛОС</samp><h2>Голосования</h2></div><button className="text-action" onClick={() => onNavigate("/votes")}>Все</button></header>
            <div className="decision-list">
              {resource.data?.votes.slice(0, 5).map((vote) => (
                <button key={vote.id} onClick={() => onNavigate("/votes")}>
                  <GavelIcon size={19} />
                  <span><strong>Пользователь {vote.target_user_id}</strong><small>{vote.chat_title || `Чат ${vote.chat_id}`}</small></span>
                  <samp>{vote.votes_for}:{vote.votes_against}</samp>
                </button>
              ))}
              {!resource.data?.votes.length && <EmptyState title="Голосований нет" text="Сейчас ничего решать не нужно." />}
            </div>
          </section>

          <section className="data-panel recent-bans-panel">
            <header className="panel-header"><div><samp>ПОСЛЕДНИЕ</samp><h2>Блокировки</h2></div><button className="text-action" onClick={() => onNavigate("/logs")}>Журнал</button></header>
            <DataTable className="data-table compact-table">
              <TableHead><span>Пользователь</span><span>Чат</span><span>Действие</span><span>Время</span></TableHead>
              {resource.data?.bans.items.slice(0, 8).map((entry) => (
                <TableRow key={entry.id} onClick={() => onNavigate(`/users/${entry.user_id}`)}>
                  <span className="mono">{entry.user_id}</span>
                  <span className="mono">{entry.chat_id}</span>
                  <span><StatusTag tone={entry.action === "ban" || entry.action === "mute" ? "danger" : "ok"}>{entry.action}</StatusTag></span>
                  <span>{formatDate(entry.timestamp)}</span>
                </TableRow>
              ))}
              {!resource.data?.bans.items.length && <TableEmptyState title="Блокировок пока нет" text="Новые записи появятся здесь." />}
            </DataTable>
          </section>

          {overview?.top_chats.length ? (
            <section className="data-panel top-chats-panel">
              <header className="panel-header"><div><samp>30 ДНЕЙ</samp><h2>Чаты с частыми срабатываниями</h2></div><WarningOctagonIcon size={24} weight="thin" /></header>
              <div className="rank-list">
                {overview.top_chats.map((chat, index) => (
                  <button key={chat.chat_id} onClick={() => onNavigate(`/chats/${chat.chat_id}`)}>
                    <samp>{String(index + 1).padStart(2, "0")}</samp><span>{chat.title || `Чат ${chat.chat_id}`}</span><strong>{formatNumber(chat.detections)}</strong>
                  </button>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}
    </>
  );
}
